import { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "app/store";
import { btnDelay, useScale } from "common/utils";
import { Select } from "common/elements/Select/Select";
import parentI18n from "features/shared/i18n";
import { SSO } from "./SSO";
import { useAuthLocale } from "./useAuthLocale";
import { BrandLogo } from "./components/BrandLogo";

export const LoginScreen = () => {
  const navigate = useNavigate();
  const { width, height, scale } = useScale();
  const { currentLocale, changeLocale, localeOptions, localeLabels } =
    useAuthLocale();
  const [localeOpen, setLocaleOpen] = useState(false);
  const langBtnRef = useRef<HTMLButtonElement>(null);
  const parent = useSelector((state: RootState) => state.user.parent);

  const handleEmailLogin = () => {
    btnDelay(() => {
      navigate("/auth/email-login");
    });
  };

  const handleQrLogin = () => {
    btnDelay(() => {
      navigate("/auth/qr-login");
    });
  };

  const handleToggleLocale = () => {
    btnDelay(() => {
      setLocaleOpen(!localeOpen);
    });
  };

  const handleCloseLocale = () => {
    setLocaleOpen(false);
  };

  const handleChangeLocale = (locale: string) => {
    changeLocale(locale);
  };

  return (
    <div className="flex-col-el w-full h-full bg-gray2-30">
      {/* メインコンテンツ */}
      <div
        style={{ height }}
        className="flex-col-el items-center justify-start w-full bg-white/40"
      >
        <main
          style={{
            width: width / scale,
            height: height / scale,
            transform: `scale(${scale})`,
          }}
          className="flex-col-el flex-center shrink-0 p-6 origin-top overflow-y-auto"
        >
          <div
            className="flex-col-view self-center"
            style={{
              width: 497,
              height: 109,
              marginBottom: 16,
            }}
          >
            <BrandLogo />
          </div>

          <div className="flex-col-el flex-center bg-white rounded-2xl p-8">
            <p className="text-[24px] text-textcolor-black text-center mb-4">
              {parentI18n.t("MSG_SIGN_IN_BTN")}
            </p>
            <div className="flex-col-el flex-center max-w-sm w-full gap-6">
              <button
                className="w-[375px] py-3.5 bg-green-100 text-white rounded-xl text-[18px] border-none cursor-pointer hover:opacity-90 transition-all"
                onClick={handleEmailLogin}
              >
                {parentI18n.t("MSG_SIGN_IN_WITH_MAIL")}
              </button>
              <SSO signIn={true} />
              {!parent && (
                <>
                  <div className="flex-row-el items-center w-[375px] gap-3">
                    <div className="flex-1 h-px bg-gray2-60" />
                    <span className="text-[16px] text-gray2-100">
                      {parentI18n.t("MSG_AUTH_OR_DIVIDER")}
                    </span>
                    <div className="flex-1 h-px bg-gray2-60" />
                  </div>
                  <button
                    type="button"
                    className="flex-row-el items-center justify-center gap-2 w-[375px] py-3.5 bg-white box-border border border-solid border-gray2-60 text-textcolor-black rounded-[12px] text-[18px] cursor-pointer hover:bg-gray2-20 transition-all"
                    onClick={handleQrLogin}
                  >
                    <span className="material-symbols-outlined">
                      qr_code_scanner
                    </span>
                    {parentI18n.t("MSG_QR_LOGIN_SCAN_BTN")}
                  </button>
                </>
              )}
            </div>

            {/* 新規登録リンク */}
            <div className="flex-row-el flex-center mt-6">
              <button
                className="bg-white border border-gray2-60 text-textcolor-black rounded-[12px] py-2 px-4 text-[16px] cursor-pointer hover:bg-gray2-20 transition-all"
                onClick={() => {
                  btnDelay(() => {
                    navigate("/auth/register");
                  });
                }}
              >
                {parentI18n.t("MSG_SIGN_NO_ACCOUNT_REGISTER")}
              </button>
            </div>

            {/* 言語切り替えボタン */}
            <div className="flex-row-el flex-center mt-28">
              <div className="flex-row-el items-center gap-3">
                <span className="text-[20px] text-textcolor-black w-[100px] text-right">
                  {parentI18n.t("MSG_PARENT_PAGE_DISPLAY_LANGUAGE")}
                </span>
                <button
                  ref={langBtnRef}
                  onClick={handleToggleLocale}
                  className="border-0 bg-green-100 flex-row-el items-center gap-2 px-6 py-3 rounded-full backdrop-blur-sm shadow-md cursor-pointer transition-all w-[200px]"
                >
                  <span className="material-symbols-outlined text-white !text-[28px]">
                    language
                  </span>
                  <span className="text-body text-white text-[20px] flex-1 text-center">
                    {localeLabels[currentLocale] || localeLabels["ja_kanji"]}
                  </span>
                  <span className="material-symbols-outlined text-white !text-[20px]">
                    expand_more
                  </span>
                </button>
                <span className="w-[100px]" />
              </div>
            </div>

            <Select
              anchorRef={langBtnRef}
              open={localeOpen}
              onClose={handleCloseLocale}
              value={currentLocale}
              onChange={handleChangeLocale}
              options={[...localeOptions]}
            />
          </div>
        </main>
      </div>
    </div>
  );
};
